import { Inbox } from "lucide-react";
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
} from "react";
import { AvatarFallback, Avatar as ShadcnAvatar } from "./components/ui/avatar";
import { Badge as ShadcnBadge } from "./components/ui/badge";
import { Button as ShadcnButton } from "./components/ui/button";
import { Card as ShadcnCard } from "./components/ui/card";
import { Checkbox as ShadcnCheckbox } from "./components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "./components/ui/empty";
import { Input as ShadcnInput } from "./components/ui/input";
import { NativeSelect } from "./components/ui/native-select";
import {
  Select as RadixSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Skeleton } from "./components/ui/skeleton";
import { Switch as ShadcnSwitch } from "./components/ui/switch";
import { Table as ShadcnTable } from "./components/ui/table";
import { Textarea as ShadcnTextarea } from "./components/ui/textarea";
import { cn } from "./lib/utils";

export { cn } from "./lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "icon";

const buttonVariants: Record<Variant, ComponentProps<typeof ShadcnButton>["variant"]> = {
  primary: "default",
  secondary: "secondary",
  ghost: "ghost",
  danger: "destructive",
};

const buttonSizes: Record<Size, ComponentProps<typeof ShadcnButton>["size"]> = {
  sm: "sm",
  md: "default",
  icon: "icon",
};

export type ButtonProps = Omit<
  ComponentProps<typeof ShadcnButton>,
  "color" | "size" | "variant"
> & {
  variant?: Variant;
  size?: Size;
};

export function Button({
  asChild = false,
  variant = "primary",
  size = "md",
  className,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <ShadcnButton
      asChild={asChild}
      className={cn("q-button", `q-button-${variant}`, `q-button-${size}`, className)}
      size={buttonSizes[size]}
      type={asChild ? undefined : type}
      variant={buttonVariants[variant]}
      {...props}
    />
  );
}

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
};

const badgeVariants: Record<
  NonNullable<BadgeProps["tone"]>,
  ComponentProps<typeof ShadcnBadge>["variant"]
> = {
  neutral: "secondary",
  accent: "default",
  success: "outline",
  warning: "outline",
  danger: "destructive",
};

export function Badge({ tone = "neutral", className, ...props }: BadgeProps) {
  return (
    <ShadcnBadge
      className={cn("q-badge", `q-badge-${tone}`, className)}
      variant={badgeVariants[tone]}
      {...props}
    />
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <ShadcnCard className={cn("q-card gap-0 overflow-visible py-0", className)} {...props} />;
}

export function MetricCard({
  label,
  value,
  tone,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  label: string;
  value: string | number;
  tone?: "accent" | "agent" | "warning" | "danger";
}) {
  return (
    <Card className={cn("q-metric-card metric-card", className)} data-tone={tone} {...props}>
      <span className="q-metric-label">{label}</span>
      <strong className="q-metric-value">{value}</strong>
      {children}
    </Card>
  );
}

export function EmptyState({
  title,
  description,
  icon = <Inbox size={18} />,
  action,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Empty className={cn("q-empty-state empty-state", className)} {...props}>
      <EmptyHeader className="q-empty-header">
        <EmptyMedia aria-hidden="true" className="q-empty-icon" variant="icon">
          {icon}
        </EmptyMedia>
        <EmptyTitle className="q-empty-title">{title}</EmptyTitle>
        {description ? (
          <EmptyDescription className="q-empty-description">{description}</EmptyDescription>
        ) : null}
      </EmptyHeader>
      {action}
    </Empty>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <div aria-busy="true" className="q-loading-state" role="status">
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="q-loading-rows">
        <Skeleton className="q-loading-row" />
        <Skeleton className="q-loading-row" />
        <Skeleton className="q-loading-row" />
      </div>
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <ShadcnInput className={cn("q-input", className)} ref={ref} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return <ShadcnTextarea className={cn("q-input q-textarea", className)} ref={ref} {...props} />;
});

export type CheckboxProps = ComponentProps<typeof ShadcnCheckbox>;

export function Checkbox({ className, ...props }: CheckboxProps) {
  return <ShadcnCheckbox className={cn("q-checkbox", className)} {...props} />;
}

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  size?: "sm" | "default";
};

export function Select({ className, ...props }: SelectProps) {
  return <NativeSelect className={cn("q-select", className)} {...props} />;
}

export function InlineSelect({
  label,
  value,
  options,
  children,
  id,
  disabled,
  onValueChange,
  ref,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  children?: ReactNode;
  id?: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <RadixSelect disabled={disabled} value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={label}
        className="q-inline-select-trigger"
        id={id}
        ref={ref}
        size="sm"
      >
        <SelectValue>{children}</SelectValue>
      </SelectTrigger>
      <SelectContent align="end" className="q-inline-select-content" position="popper">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <span className="q-inline-select-option">{option.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </RadixSelect>
  );
}

export type SwitchProps = ComponentProps<typeof ShadcnSwitch> & {
  label: string;
};

export function Switch({ className, id, label, ...props }: SwitchProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;

  return (
    <div className={cn("q-switch", className)}>
      <ShadcnSwitch id={resolvedId} {...props} />
      <label className="q-switch-label" htmlFor={resolvedId}>
        {label}
      </label>
    </div>
  );
}

export function Avatar({
  className,
  children,
  size = "md",
  ...props
}: HTMLAttributes<HTMLDivElement> & { size?: "sm" | "md" }) {
  return (
    <ShadcnAvatar className={cn("q-avatar", `q-avatar-${size}`, className)} {...props}>
      <AvatarFallback>{children}</AvatarFallback>
    </ShadcnAvatar>
  );
}

export function Table({ className, ...props }: ComponentPropsWithoutRef<"table">) {
  return <ShadcnTable className={cn("q-table", className)} {...props} />;
}

export function Modal({
  open,
  title,
  children,
  closeLabel = "Close",
  initialFocusId,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  closeLabel?: string;
  initialFocusId?: string;
  onClose: () => void;
}) {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) return undefined;

    const rememberFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && event.target !== document.body) {
        returnFocusRef.current = event.target;
      }
    };

    document.addEventListener("focusin", rememberFocus);
    return () => document.removeEventListener("focusin", rememberFocus);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) return;
        const returnTarget = returnFocusRef.current;
        onClose();
        requestAnimationFrame(() => {
          if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
        });
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="q-modal max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-[640px]"
        closeLabel={closeLabel}
        onOpenAutoFocus={(event) => {
          if (
            document.activeElement instanceof HTMLElement &&
            document.activeElement !== document.body
          ) {
            returnFocusRef.current = document.activeElement;
          }
          if (!initialFocusId) return;
          const initialFocus = document.getElementById(initialFocusId);
          if (initialFocus === null) return;
          event.preventDefault();
          initialFocus.focus({ preventScroll: true });
        }}
      >
        <DialogHeader className="q-modal-header">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}
